import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent, type DragEvent } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import { useApiClient } from "../../api/ApiClientContext";
import type { FilesResponse, PartnerFolder } from "../../api/types";
import { ApiError } from "../../api/types";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { ExplorerBreadcrumbs } from "../../components/explorer/ExplorerBreadcrumbs";
import { ExplorerLayout } from "../../components/explorer/ExplorerLayout";
import { ExplorerTable, type ExplorerRow } from "../../components/explorer/ExplorerTable";
import { ExplorerGrid } from "../../components/explorer/ExplorerGrid";
import { ExplorerToolbar } from "../../components/explorer/ExplorerToolbar";
import { ExplorerUploadStatus } from "../../components/explorer/ExplorerUploadStatus";
import { FileTree } from "../../components/explorer/FileTree";
import { NewFolderDialog } from "../../components/explorer/NewFolderDialog";
import { UploadRejectedDialog } from "../../components/explorer/UploadRejectedDialog";
import { useContextMenu } from "../../hooks/useContextMenu";
import { useExplorerUrlState } from "../../hooks/useExplorerUrlState";
import { useFileUpload } from "../../hooks/useFileUpload";
import { downloadAll } from "../../utils/downloadAll";
import {
  anyRowHasAction,
  copyNameAction,
  deleteFolderAction,
  downloadAction,
  newFolderAction,
  openAction,
  refreshAction,
  runSelectedActions,
  uploadHereAction,
  type RowAction,
} from "../../utils/explorerActions";
import { filterRowsByName, mostRecent, sortRows } from "../../utils/explorerSort";
import { depthAt, joinPath, MAX_FOLDER_DEPTH } from "../../utils/pathRules";

const FOLDERS: { key: PartnerFolder; label: string }[] = [
  { key: "LEEFT_TO_PARTNER", label: "LEEFT_TO_PARTNER" },
  { key: "PARTNER_TO_LEEFT", label: "PARTNER_TO_LEEFT" },
];

/** Narrows the URL's raw `folder` string to a real PartnerFolder, defensively (a hand-edited URL cannot smuggle an arbitrary value into a typed API call). */
function asPartnerFolder(value: string | null): PartnerFolder | null {
  return value === "LEEFT_TO_PARTNER" || value === "PARTNER_TO_LEEFT" ? value : null;
}

/**
 * The partner's "Your Files" explorer (files-explorer-reference.png
 * layout, Leeft branding): a sidebar with LEEFT_TO_PARTNER ("From Leeft",
 * navigate and download, including a member's own delivered copies) and
 * PARTNER_TO_LEEFT ("Send to Leeft", upload, with subfolders - ADR 0010),
 * and a sortable, searchable table for whichever is selected. Both mounts
 * are now plain listings (ADR 0011): a rejected upload is refused
 * synchronously, before it is ever stored, and never appears here.
 */
export function PartnerFilesPage() {
  const apiClient = useApiClient();
  const { folder, path, sort, dir, view, setFolder, setPath, navigate, toggleSort, setView } =
    useExplorerUrlState();
  const mount = asPartnerFolder(folder);
  const [files, setFiles] = useState<FilesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [dragOver, setDragOver] = useState(false);
  const [newFolderParent, setNewFolderParent] = useState<string | null>(null);
  const [deleteFolderTarget, setDeleteFolderTarget] = useState<string | null>(null);
  const [deleteFolderError, setDeleteFolderError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const paneMenu = useContextMenu();
  const latestRequestRef = useRef(0);

  // ADR 0010: `folder`/`path` are reactive URL state, so the listing must
  // reload on every navigation, not just on mount; a request counter
  // discards a stale response that resolves after a newer navigation.
  const load = useCallback(async () => {
    const requestId = ++latestRequestRef.current;
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.listFiles(mount ? { folder: mount, path } : undefined);
      if (latestRequestRef.current === requestId) {
        setFiles(response);
      }
    } catch (caught) {
      if (latestRequestRef.current === requestId) {
        setError(caught instanceof ApiError ? caught.message : "Could not load your files.");
      }
    } finally {
      if (latestRequestRef.current === requestId) {
        setLoading(false);
      }
    }
  }, [apiClient, mount, path]);

  // Fetching data in response to a prop/URL change is exactly the effect
  // React's own docs endorse (unlike deriving state that render could
  // just compute): load() cannot run during render since it is async.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load();
  }, [load]);

  const {
    upload,
    error: uploadError,
    notice: uploadNotice,
    rejected: uploadRejected,
    retryFile,
    start,
    cancel,
    dismissRejected,
  } = useFileUpload(
    (file, onProgress) => apiClient.uploadPartnerFile(file, path, onProgress),
    () => void load(),
  );

  // Resets the search box and the selection when the folder or path
  // changes, without an Effect (React docs: "Adjusting state when a prop
  // changes").
  const [previousLocation, setPreviousLocation] = useState(`${folder ?? ""} ${path}`);
  const currentLocation = `${folder ?? ""} ${path}`;
  if (currentLocation !== previousLocation) {
    setPreviousLocation(currentLocation);
    setSearch("");
    setSelected(new Set());
  }

  const uploadEnabled = folder === "PARTNER_TO_LEEFT";
  const newFolderEnabled = folder === "PARTNER_TO_LEEFT" && depthAt(path) <= MAX_FOLDER_DEPTH;

  // A stable function value (not a fresh ref-reading closure built during
  // render) so it can be handed to row/pane actions built while rendering.
  const triggerFileInput = useCallback(() => fileInputRef.current?.click(), []);

  function uploadInto(targetPath: string) {
    if (folder !== "PARTNER_TO_LEEFT" || path !== targetPath) {
      navigate("PARTNER_TO_LEEFT", targetPath);
    }
    triggerFileInput();
  }

  function handleFileInputChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (file) {
      start(file);
    }
  }

  function handleDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    setDragOver(false);
    if (!uploadEnabled || upload) {
      return;
    }
    const file = event.dataTransfer.files[0];
    if (file) {
      start(file);
    }
  }

  function toggleSelect(key: string) {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }

  async function handleCreateFolder(name: string) {
    const parent = newFolderParent ?? "";
    await apiClient.createFolder(joinPath(parent, name));
    setNewFolderParent(null);
    await load();
  }

  async function handleDeleteFolderConfirmed() {
    if (deleteFolderTarget === null) {
      return;
    }
    setDeleteFolderError(null);
    try {
      await apiClient.deleteFolder(deleteFolderTarget);
      setDeleteFolderTarget(null);
      await load();
    } catch (caught) {
      setDeleteFolderError(caught instanceof ApiError ? caught.message : "Could not delete the folder.");
    }
  }

  /**
   * The shared action model for a folder, wherever it appears (a root
   * mount row, a subfolder table/grid row, or a tree node): ADR 0010's
   * per-role, per-mount rules, so no menu can ever offer an action the
   * server would refuse. `isMountRoot` is true only for the top-level
   * LEEFT_TO_PARTNER/PARTNER_TO_LEEFT row itself, which can never be
   * deleted.
   */
  function buildFolderActions(mount: PartnerFolder, targetPath: string, isMountRoot: boolean): RowAction[] {
    const actions: RowAction[] = [openAction(() => navigate(mount, targetPath))];
    if (mount !== "PARTNER_TO_LEEFT") {
      // Partners only navigate and download in LEEFT_TO_PARTNER; staff
      // manage it (StaffSharingFilesTab).
      return actions;
    }
    actions.push(uploadHereAction(() => uploadInto(targetPath)));
    if (depthAt(targetPath) <= MAX_FOLDER_DEPTH) {
      actions.push(newFolderAction(() => setNewFolderParent(targetPath)));
    }
    if (!isMountRoot) {
      actions.push(deleteFolderAction(() => setDeleteFolderTarget(targetPath)));
    }
    return actions;
  }

  // --- Root: the folder list itself -----------------------------------
  const rootRows: ExplorerRow[] = useMemo(() => {
    // triggerFileInput (a stable useCallback, reached through
    // buildFolderActions -> uploadInto) only runs later, from a menu
    // item's onClick; the lint rule cannot see through that call chain
    // to confirm that, hence the disable.
    // eslint-disable-next-line react-hooks/refs
    return FOLDERS.map((folderDef) => {
      const folderFiles = folderDef.key === "LEEFT_TO_PARTNER" ? (files?.available ?? []) : (files?.delivered ?? []);
      return {
        key: folderDef.key,
        name: folderDef.label,
        kind: "folder" as const,
        lastModified: mostRecent(folderFiles.map((f) => f.lastModified)),
        size: null,
        onActivate: () => setFolder(folderDef.key),
        actions: [...buildFolderActions(folderDef.key, "", true), refreshAction(() => void load())],
      };
    });
    // buildFolderActions is a plain function re-created every render (not
    // itself memoised), so listing it here just means this memo
    // recomputes whenever it does too - the same as any of its own
    // captured values (folder, path) changing.
  }, [files, setFolder, load, buildFolderActions]);

  // --- Inside a folder ---------------------------------------------------
  // Both mounts are now plain listings (ADR 0011): list and download.
  // Only PARTNER_TO_LEEFT offers upload, new folder and delete-folder
  // (buildFolderActions above); a partner never gets a Delete action on a
  // file in either mount.
  const folderRows: ExplorerRow[] = useMemo(() => {
    if (!files || !mount) {
      return [];
    }
    // See the eslint-disable above rootRows.
    // eslint-disable-next-line react-hooks/refs
    const subfolders: ExplorerRow[] = files.folders.map((info) => {
      const childPath = joinPath(path, info.name);
      return {
        key: `folder:${info.name}`,
        name: info.name,
        kind: "folder" as const,
        lastModified: null,
        size: null,
        onActivate: () => setPath(childPath),
        actions: buildFolderActions(mount, childPath, false),
      };
    });

    // `?? []` is defensive (manager report: a BFF response can send a
    // JSON null, not [], for a nil Go slice).
    const items = (mount === "LEEFT_TO_PARTNER" ? files.available : files.delivered) ?? [];
    const downloadUrl =
      mount === "LEEFT_TO_PARTNER" ? apiClient.partnerDownloadUrl : apiClient.partnerDeliveredDownloadUrl;
    const fileRows: ExplorerRow[] = items.map((file) => {
      const href = downloadUrl(file.name, path);
      return {
        key: file.name,
        name: file.name,
        kind: "file" as const,
        lastModified: file.lastModified,
        size: file.size,
        href,
        actions: [downloadAction(() => downloadAll([href])), copyNameAction(file.name)],
      };
    });
    return [...subfolders, ...fileRows];
  }, [files, mount, path, apiClient, setPath, buildFolderActions]);

  const displayedRows = useMemo(() => {
    const rows = folder ? folderRows : rootRows;
    return sortRows(filterRowsByName(rows, search), sort, dir);
  }, [folder, folderRows, rootRows, search, dir, sort]);

  const selectedRows = displayedRows.filter((row) => selected.has(row.key));
  const canDownloadSelected = anyRowHasAction(selectedRows, "download");
  const folderLabel = FOLDERS.find((f) => f.key === folder)?.label ?? null;

  // See the eslint-disable above rootRows: triggerFileInput is only
  // invoked later, from the pane menu's onClick.
  /* eslint-disable react-hooks/refs */
  const paneActions: RowAction[] = [
    ...(uploadEnabled ? [uploadHereAction(triggerFileInput, "Upload file")] : []),
    ...(newFolderEnabled ? [newFolderAction(() => setNewFolderParent(path))] : []),
    refreshAction(() => void load()),
  ];
  /* eslint-enable react-hooks/refs */

  const treeFolderActions = new Map(rootRows.map((row) => [row.key, row.actions ?? []]));

  return (
    <ExplorerLayout
      sidebar={
        <FileTree
          folders={FOLDERS}
          selectedFolder={folder}
          selectedPath={path}
          onSelectRoot={() => setFolder(null)}
          onSelect={(nextFolder, nextPath) => navigate(nextFolder, nextPath)}
          loadChildren={(folderKey, at) =>
            apiClient
              .listFiles({ folder: folderKey as PartnerFolder, path: at })
              .then((response) => response.folders.map((info) => info.name))
          }
          refreshKey={files}
          actionsForNode={(folderKey, at) => {
            if (folderKey === null) {
              return [refreshAction(() => void load())];
            }
            if (at === "") {
              return treeFolderActions.get(folderKey) ?? [];
            }
            return buildFolderActions(folderKey as PartnerFolder, at, false);
          }}
        />
      }
    >
      <ExplorerToolbar
        uploadEnabled={uploadEnabled}
        onUpload={triggerFileInput}
        newFolderEnabled={folder === "PARTNER_TO_LEEFT" ? newFolderEnabled : undefined}
        onNewFolder={folder === "PARTNER_TO_LEEFT" ? () => setNewFolderParent(path) : undefined}
        canDownloadSelected={canDownloadSelected}
        canDeleteSelected={false}
        showDeleteAction={false}
        onDownloadSelected={() => runSelectedActions(displayedRows, selected, "download")}
        onDeleteSelected={() => {}}
        onRefresh={() => void load()}
        search={search}
        onSearchChange={setSearch}
        view={view}
        onToggleView={() => setView(view === "list" ? "grid" : "list")}
      />
      <input
        ref={fileInputRef}
        type="file"
        hidden
        aria-label="Choose a file to send to Leeft"
        onChange={handleFileInputChange}
      />

      <ExplorerUploadStatus
        upload={upload}
        error={uploadError}
        notice={uploadNotice}
        retryFile={retryFile}
        onCancel={cancel}
        onRetry={start}
      />

      <Box
        onDragOver={(event) => {
          event.preventDefault();
          if (uploadEnabled && !upload) {
            setDragOver(true);
          }
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onContextMenu={paneMenu.open}
        sx={{
          flexGrow: 1,
          px: 2,
          pb: 2,
          outline: dragOver ? "2px dashed" : "none",
          outlineColor: "primary.main",
          outlineOffset: -4,
        }}
      >
        <ExplorerBreadcrumbs
          rootLabel="Your Files"
          folderLabel={folderLabel}
          path={path}
          onNavigateRoot={() => setFolder(null)}
          onNavigateFolder={() => setPath("")}
          onNavigatePath={setPath}
        />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {folder === "LEEFT_TO_PARTNER" && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Files Leeft has shared with you. Click a name to download it.
          </Typography>
        )}
        {folder === "PARTNER_TO_LEEFT" && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Drag and drop a file here, or use Upload above. Files are checked for malware before
            being added here; you can download your own uploads back at any time.
          </Typography>
        )}

        {view === "grid" ? (
          <ExplorerGrid
            caption={folder ? `${folderLabel} files` : "Your Files"}
            rows={displayedRows}
            loading={loading}
            emptyMessage={
              folder
                ? folder === "LEEFT_TO_PARTNER"
                  ? "Leeft has not shared any files with you yet."
                  : "You have not sent any files to Leeft yet."
                : "No folders yet."
            }
            selectedKeys={folder ? selected : undefined}
            onToggleSelect={folder ? toggleSelect : undefined}
          />
        ) : (
          <ExplorerTable
            caption={folder ? `${folderLabel} files` : "Your Files"}
            rows={displayedRows}
            loading={loading}
            emptyMessage={
              folder
                ? folder === "LEEFT_TO_PARTNER"
                  ? "Leeft has not shared any files with you yet."
                  : "You have not sent any files to Leeft yet."
                : "No folders yet."
            }
            sort={sort}
            dir={dir}
            onSort={toggleSort}
            selectedKeys={folder ? selected : undefined}
            onToggleSelect={folder ? toggleSelect : undefined}
            onToggleSelectAll={
              folder
                ? () =>
                    setSelected((current) =>
                      current.size === displayedRows.length ? new Set() : new Set(displayedRows.map((r) => r.key)),
                    )
                : undefined
            }
          />
        )}
      </Box>

      <Menu
        open={paneMenu.position !== null}
        onClose={paneMenu.close}
        anchorReference="anchorPosition"
        anchorPosition={paneMenu.position ?? undefined}
      >
        {paneActions.map((action) => (
          <MenuItem
            key={action.key}
            onClick={() => {
              paneMenu.close();
              action.onSelect();
            }}
          >
            {action.label}
          </MenuItem>
        ))}
      </Menu>

      <UploadRejectedDialog file={uploadRejected} onClose={dismissRejected} />

      <NewFolderDialog
        open={newFolderParent !== null}
        parentPath={newFolderParent ?? ""}
        onClose={() => setNewFolderParent(null)}
        onCreate={handleCreateFolder}
      />

      <ConfirmDialog
        open={deleteFolderTarget !== null}
        title="Delete folder"
        description={`This deletes the empty folder "${deleteFolderTarget}". This cannot be undone.`}
        confirmLabel="Delete folder"
        destructive
        error={deleteFolderError}
        onConfirm={() => handleDeleteFolderConfirmed()}
        onClose={() => {
          setDeleteFolderTarget(null);
          setDeleteFolderError(null);
        }}
      />
    </ExplorerLayout>
  );
}
